/* 
   clickup_api.js
   Dein zentrales Modul für alle ClickUp-API-Aufrufe
   (Frontend -> Supabase Edge Function Proxy -> ClickUp)
*/

// Supabase Edge Function Endpoint (kann per ENV überschrieben werden)
const SUPABASE_FUNCTION_URL =
  process.env.REACT_APP_SUPABASE_FUNCTION_URL ||
  'https://hhbmrjtnzatwhrzkqedk.supabase.co/functions/v1/clickup-backend';

// Supabase Anon Key (wird für den Aufruf der Edge Function genutzt)
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

function buildHeaders(clickupApiKey) {
  // Custom Header Wert bevorzugt aus ENV, fallback auf workspace_id aus localStorage
  let customHeader = process.env.REACT_APP_CUSTOM_HEADER || '';
  try {
    if (!customHeader && typeof localStorage !== 'undefined') {
      const u = JSON.parse(localStorage.getItem('user'));
      customHeader = u?.workspace_id?.[0] || u?.rec_id || '';
    }
  } catch (_) {
    // ignore
  }

  const headers = {
    // Wichtig: Authorization hier ist NUR für Supabase (nicht für ClickUp)
    Authorization: SUPABASE_ANON_KEY ? `Bearer ${SUPABASE_ANON_KEY}` : '',
    'Content-Type': 'application/json',
    // ClickUp API Key separat, damit keine Überschneidung mit Supabase-Auth entsteht
    'x-clickup-token': clickupApiKey,
  };
  if (customHeader) headers['x-custom-header'] = customHeader;
  return headers;
}

export async function getSpaces(teamId, apiKey) {
  const resp = await fetch(
    `${SUPABASE_FUNCTION_URL}/spaces?teamId=${encodeURIComponent(teamId)}`,
    {
      method: 'GET',
      headers: buildHeaders(apiKey),
    }
  );
  if (!resp.ok) {
    throw new Error(`Fehler beim Laden der Spaces: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data?.spaces || [];
}

export async function getFolders(spaceId, apiKey) {
  const resp = await fetch(
    `${SUPABASE_FUNCTION_URL}/folders?spaceId=${encodeURIComponent(spaceId)}`,
    {
      method: 'GET',
      headers: buildHeaders(apiKey),
    }
  );
  if (!resp.ok) {
    throw new Error(`Fehler beim Laden der Folders: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data?.folders || [];
}

// Folderlose Listen
export async function getFolderlessLists(spaceId, apiKey) {
  const resp = await fetch(
    `${SUPABASE_FUNCTION_URL}/folderless-lists?spaceId=${encodeURIComponent(spaceId)}`,
    {
      method: 'GET',
      headers: buildHeaders(apiKey),
    }
  );
  if (!resp.ok) {
    throw new Error(`Fehler beim Laden der folderless Lists: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data?.lists || [];
}

// Listen in einem bestimmten Ordner
export async function getFolderLists(folderId, apiKey) {
  const resp = await fetch(
    `${SUPABASE_FUNCTION_URL}/folder-lists?folderId=${encodeURIComponent(folderId)}`,
    {
      method: 'GET',
      headers: buildHeaders(apiKey),
    }
  );
  if (!resp.ok) {
    throw new Error(`Fehler beim Laden der Folder Lists: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data?.lists || [];
}

// Holt die Basisdaten einer Liste, inklusive Status
export async function getListData(listId, apiKey) {
  const resp = await fetch(
    `${SUPABASE_FUNCTION_URL}/list/${encodeURIComponent(listId)}`,
    {
      method: 'GET',
      headers: buildHeaders(apiKey),
    }
  );
  if (!resp.ok) {
    throw new Error(`Fehler beim Laden der List-Daten: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data;
}

// Holt die Mitglieder einer Liste
export async function getListMembers(listId, apiKey) {
  const resp = await fetch(
    `${SUPABASE_FUNCTION_URL}/list/${encodeURIComponent(listId)}/members`,
    {
      method: 'GET',
      headers: buildHeaders(apiKey),
    }
  );
  if (!resp.ok) {
    throw new Error(`Fehler beim Laden der List-Members: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data?.members || [];
}

// Holt Aufgaben einer Liste (optional ohne Subtasks)
export async function getListTasks(listId, apiKey, { includeSubtasks = false, page = 0, archived = false } = {}) {
  const params = new URLSearchParams();
  params.append('subtasks', includeSubtasks ? 'true' : 'false');
  params.append('page', String(page));
  params.append('archived', archived ? 'true' : 'false');

  const resp = await fetch(
    `${SUPABASE_FUNCTION_URL}/list/${encodeURIComponent(listId)}/tasks?${params.toString()}`,
    {
      method: 'GET',
      headers: buildHeaders(apiKey),
    }
  );
  if (!resp.ok) {
    throw new Error(`Fehler beim Laden der Tasks: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data?.tasks || [];
}